import { useEffect, useState } from "react";
import { CAMPO } from "./ui";
import { PartesEditor } from "./PeritoEditores";
import { Alternador, BOTAO_PERIGO, BOTAO_SECUNDARIO, Campo, Dialogo, IconeMais } from "./PeritoUi";
import { aplicarMascaraCnj, parseCnj } from "../lib/perito/cnj";
import { cpfCnpjValido, mascaraCpfCnpj } from "../lib/perito/cpfCnpj";
import { hoje, maisDias } from "../lib/perito/datas";
import { formatBRL, PRIORIDADE_LABEL, TAREFA_LABEL } from "../lib/perito/format";
import { TEMPLATES_TAREFA, buscarModelo } from "../lib/perito/modelos";
import { ETAPAS, ETAPA_LABEL, etapaNumDe, type EtapaNum } from "../lib/perito/pipeline";
import { usePerito } from "../lib/perito/store";
import type { ChecklistItem, Honorario, Parte, Pericia, Prazo, Prioridade, StatusTarefa, Tarefa } from "../lib/perito/tipos";
import { TIPOS_PERICIA, TRIBUNAIS } from "../lib/perito/tribunais";

// Os diálogos da Central do Perito (000385), portados de `src/components/dialogs/` de lá: a perícia, a
// tarefa, o prazo e o honorário. Moram juntos porque a ficha os usa agora e as telas de Prazos, Tarefas e
// Financeiro (a fase B) vão usá-los de novo.
//
// A validação é a de lá MAIS a do banco: o que o banco recusa, o diálogo recusa antes, com a frase. Uma
// recusa na fila desfaz a alteração na tela inteira (a fila recarrega), e o aluno perderia o que digitou.

const PRIORIDADES = Object.keys(PRIORIDADE_LABEL) as Prioridade[];
const nomeCurto = (p: Pericia) => p.apelido || p.numeroProcesso || "(sem número)";

/** Um campo de valor em reais: vazio é nulo, vírgula vale como ponto, e o negativo não passa. */
function numeroDoCampo(v: string): number | null {
  if (!v.trim()) return null;
  const n = Number.parseFloat(v.replace(",", "."));
  return Number.isFinite(n) ? Math.round(n * 100) / 100 : null;
}

function Erro({ texto }: { texto: string | null }) {
  return texto ? <p role="alert" className="rounded-xl bg-erro-fundo p-3 text-sm text-erro-tinta" data-erro-dialogo>{texto}</p> : null;
}

function Rodape({ aoExcluir, rotuloExcluir, aoFechar, aoSalvar }: { aoExcluir?: () => void; rotuloExcluir?: string; aoFechar: () => void; aoSalvar: () => void }) {
  return (
    <div className="flex w-full flex-wrap items-center justify-between gap-2">
      {aoExcluir ? <button type="button" className={BOTAO_PERIGO} onClick={aoExcluir} data-excluir>{rotuloExcluir}</button> : <span />}
      <div className="flex gap-2">
        <button type="button" className={BOTAO_SECUNDARIO} onClick={aoFechar}>Cancelar</button>
        <button type="button" className="pill-cta min-h-10 px-5 text-sm" onClick={aoSalvar} data-salvar>Salvar</button>
      </div>
    </div>
  );
}

// ── A perícia ───────────────────────────────────────────────────────────────────────────────────────────

export function PericiaDialogo({ pericia, aberto, aoFechar }: { pericia: Pericia; aberto: boolean; aoFechar: () => void }) {
  const s = usePerito();
  const [aba, setAba] = useState<"dados" | "partes">("dados");
  const [numero, setNumero] = useState("");
  const [apelido, setApelido] = useState("");
  const [tribunal, setTribunal] = useState("");
  const [vara, setVara] = useState("");
  const [tipo, setTipo] = useState("");
  const [prioridade, setPrioridade] = useState<Prioridade>("media");
  const [etapa, setEtapa] = useState<EtapaNum>(1);
  const [dataNomeacao, setDataNomeacao] = useState("");
  const [dataAceite, setDataAceite] = useState("");
  const [dataEntrega, setDataEntrega] = useState("");
  const [proximaAcao, setProximaAcao] = useState("");
  const [fontePagadora, setFontePagadora] = useState("");
  const [cpfCnpj, setCpfCnpj] = useState("");
  const [observacoes, setObservacoes] = useState("");
  const [partes, setPartes] = useState<Parte[]>([]);
  const [erro, setErro] = useState<string | null>(null);

  // Abriu: o formulário parte da perícia como ela está AGORA (e não como estava na última abertura).
  useEffect(() => {
    if (!aberto) return;
    setAba("dados");
    setNumero(pericia.numeroProcesso ?? "");
    setApelido(pericia.apelido ?? "");
    setTribunal(pericia.tribunal ?? "");
    setVara(pericia.vara ?? "");
    setTipo(pericia.tipoPericia ?? "");
    setPrioridade(pericia.prioridade);
    setEtapa(etapaNumDe(pericia));
    setDataNomeacao(pericia.dataNomeacao ?? "");
    setDataAceite(pericia.dataAceite ?? "");
    setDataEntrega(pericia.dataEntregaLaudo ?? "");
    setProximaAcao(pericia.proximaAcao ?? "");
    setFontePagadora(pericia.fontePagadora ?? "");
    setCpfCnpj(mascaraCpfCnpj(pericia.cpfCnpjFonte ?? ""));
    setObservacoes(pericia.observacoes ?? "");
    setPartes(pericia.partes ?? []);
    setErro(null);
    // Só na abertura: reler a cada mudança da perícia apagaria o que a pessoa está digitando.
  }, [aberto]);

  const tribunalObj = TRIBUNAIS.find((t) => t.sigla === tribunal);
  // O tribunal gravado que não está na base (a perícia veio de outra versão da lista) continua escolhível.
  const tribunais = tribunal && !tribunalObj ? [{ sigla: tribunal, nome: "(fora da lista)" }, ...TRIBUNAIS] : TRIBUNAIS;
  const varas = tribunalObj?.varas ?? [];
  const cnj = parseCnj(numero);

  const salvar = () => {
    if (!numero.trim() || !tribunal || !tipo) return setErro("Preencha o número do processo, o tribunal e o tipo de perícia.");
    if (cpfCnpj && !cpfCnpjValido(cpfCnpj)) return setErro("O CPF ou CNPJ da fonte pagadora está incompleto. Complete ou apague o campo.");
    s.updatePericia(pericia.id, {
      numeroProcesso: numero.trim(),
      apelido: apelido.trim() || undefined,
      tribunal,
      vara,
      uf: tribunalObj?.uf ?? pericia.uf,
      tipoPericia: tipo,
      prioridade,
      partes: partes.filter((p) => p.nome.trim()),
      dataNomeacao: dataNomeacao || undefined,
      dataAceite: dataAceite || undefined,
      dataEntregaLaudo: dataEntrega || undefined,
      proximaAcao: proximaAcao.trim() || undefined,
      fontePagadora: fontePagadora.trim() || undefined,
      cpfCnpjFonte: cpfCnpj || undefined,
      observacoes: observacoes.trim() || undefined,
    });
    if (etapa !== etapaNumDe(pericia)) s.moverEtapa(pericia.id, etapa);
    s.addHistorico({ acao: "Dados da perícia atualizados", pericia_id: pericia.id, detalhe: numero.trim() });
    aoFechar();
  };

  return (
    <Dialogo aberto={aberto} aoFechar={aoFechar} titulo="Editar a perícia" largo rodape={<Rodape aoFechar={aoFechar} aoSalvar={salvar} />}>
      <div className="flex flex-col gap-4" data-dialogo-pericia>
        <Alternador rotulo="Seção" opcoes={[{ id: "dados", rotulo: "Dados" }, { id: "partes", rotulo: `Partes (${partes.length})` }] as const} valor={aba} aoMudar={setAba} />
        {aba === "dados" ? (
          <>
            <Campo rotulo="Número do processo (CNJ)">
              <input className={`${CAMPO} tabular-nums`} value={numero} onChange={(e) => setNumero(aplicarMascaraCnj(e.target.value))} placeholder="0000000-00.0000.0.00.0000" inputMode="numeric" data-campo-numero />
              {numero && cnj && <span className={`text-xs font-semibold ${cnj.valido ? "text-aviso-tinta" : "text-erro-tinta"}`}>{cnj.valido ? "CNJ válido" : "O dígito verificador não confere: confira o número"}</span>}
            </Campo>
            <Campo rotulo="Apelido do processo" dica="Um nome curto para reconhecer o processo nas listas.">
              <input className={CAMPO} value={apelido} maxLength={40} placeholder="Ex.: Silva × Banco X" onChange={(e) => setApelido(e.target.value)} data-campo-apelido />
            </Campo>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Campo rotulo="Tribunal">
                <select className={CAMPO} value={tribunal} onChange={(e) => { setTribunal(e.target.value); setVara(""); }}>
                  <option value="">Escolha</option>
                  {tribunais.map((t) => <option key={t.sigla} value={t.sigla}>{t.sigla} — {t.nome}</option>)}
                </select>
              </Campo>
              <Campo rotulo="Vara">
                <select className={CAMPO} value={vara} onChange={(e) => setVara(e.target.value)} disabled={!tribunalObj && !vara}>
                  <option value="">{tribunalObj ? "Escolha" : "Escolha o tribunal antes"}</option>
                  {vara && !varas.includes(vara) && <option value={vara}>{vara}</option>}
                  {varas.map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
              </Campo>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Campo rotulo="Tipo de perícia">
                <select className={CAMPO} value={tipo} onChange={(e) => setTipo(e.target.value)}>
                  <option value="">Escolha</option>
                  {tipo && !TIPOS_PERICIA.includes(tipo) && <option value={tipo}>{tipo}</option>}
                  {TIPOS_PERICIA.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </Campo>
              <Campo rotulo="Prioridade">
                <select className={CAMPO} value={prioridade} onChange={(e) => setPrioridade(e.target.value as Prioridade)}>
                  {PRIORIDADES.map((p) => <option key={p} value={p}>{PRIORIDADE_LABEL[p]}</option>)}
                </select>
              </Campo>
            </div>
            <Campo rotulo="Etapa atual" dica="Mudar a etapa cria as tarefas sugeridas da etapa nova, menos as que você já tirou desta perícia.">
              <select className={CAMPO} value={etapa} onChange={(e) => setEtapa(Number(e.target.value) as EtapaNum)} data-campo-etapa>
                {ETAPAS.map((e) => <option key={e} value={e}>{e}. {ETAPA_LABEL[e]}</option>)}
              </select>
            </Campo>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <Campo rotulo="Nomeação"><input className={CAMPO} type="date" value={dataNomeacao} onChange={(e) => setDataNomeacao(e.target.value)} /></Campo>
              <Campo rotulo="Aceite"><input className={CAMPO} type="date" value={dataAceite} onChange={(e) => setDataAceite(e.target.value)} /></Campo>
              <Campo rotulo="Entrega do laudo"><input className={CAMPO} type="date" value={dataEntrega} onChange={(e) => setDataEntrega(e.target.value)} /></Campo>
            </div>
            <Campo rotulo="Próxima ação">
              <input className={CAMPO} value={proximaAcao} maxLength={500} placeholder="Ex.: Cobrar documentos pendentes" onChange={(e) => setProximaAcao(e.target.value)} />
            </Campo>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Campo rotulo="Fonte pagadora"><input className={CAMPO} value={fontePagadora} maxLength={200} placeholder="Quem deposita os honorários" onChange={(e) => setFontePagadora(e.target.value)} /></Campo>
              <Campo rotulo="CPF ou CNPJ da fonte pagadora">
                <input className={`${CAMPO} tabular-nums`} value={cpfCnpj} inputMode="numeric" placeholder="00.000.000/0000-00" onChange={(e) => setCpfCnpj(mascaraCpfCnpj(e.target.value))} data-campo-cpf />
                {cpfCnpj && !cpfCnpjValido(cpfCnpj) && <span className="text-xs font-semibold text-erro-tinta">Documento incompleto</span>}
              </Campo>
            </div>
            <Campo rotulo="Observações">
              <textarea className={`${CAMPO} h-32 py-3`} value={observacoes} maxLength={10000} onChange={(e) => setObservacoes(e.target.value)} />
            </Campo>
          </>
        ) : (
          <PartesEditor valor={partes} aoMudar={setPartes} />
        )}
        <Erro texto={erro} />
      </div>
    </Dialogo>
  );
}

// ── A tarefa ────────────────────────────────────────────────────────────────────────────────────────────

/** `tarefa` nula com o diálogo aberto é uma tarefa NOVA, vinculada a `periciaPadrao`. */
export function TarefaDialogo({ tarefa, aberto, periciaPadrao, aoFechar }: {
  tarefa: Tarefa | null; aberto: boolean; periciaPadrao?: string; aoFechar: () => void;
}) {
  const s = usePerito();
  const [titulo, setTitulo] = useState("");
  const [descricao, setDescricao] = useState("");
  const [prazo, setPrazo] = useState("");
  const [prioridade, setPrioridade] = useState<Prioridade>("media");
  const [status, setStatus] = useState<StatusTarefa>("pendente");
  const [periciaId, setPericiaId] = useState("");
  const [checklist, setChecklist] = useState<ChecklistItem[]>([]);
  const [novoItem, setNovoItem] = useState("");
  const [modeloId, setModeloId] = useState("");
  const [prazoSugerido, setPrazoSugerido] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (!aberto) return;
    setTitulo(tarefa?.titulo ?? "");
    setDescricao(tarefa?.descricao ?? "");
    setPrazo(tarefa?.prazo ?? "");
    setPrioridade(tarefa?.prioridade ?? "media");
    setStatus(tarefa?.status ?? "pendente");
    setPericiaId(tarefa?.pericia_id ?? periciaPadrao ?? "");
    setChecklist(tarefa?.checklist ?? []);
    setNovoItem("");
    setModeloId(tarefa?.modelo_id ?? "");
    setPrazoSugerido(!!tarefa?.prazoSugerido);
    setErro(null);
    // Só na abertura: reler a cada mudança da perícia apagaria o que a pessoa está digitando.
  }, [aberto]);

  const aplicarModelo = (id: string) => {
    setModeloId(id);
    const m = buscarModelo(id);
    if (!m) return;
    setTitulo(m.titulo);
    setDescricao(m.descricao);
    setPrioridade(m.prioridade);
    setPrazo(maisDias(hoje(), m.prazoDias));
    setPrazoSugerido(true);
    setChecklist(m.checklist.map((texto) => ({ id: crypto.randomUUID(), texto, feito: false })));
  };
  const incluirItem = () => {
    const t = novoItem.trim();
    if (!t) return;
    setChecklist((c) => [...c, { id: crypto.randomUUID(), texto: t, feito: false }]);
    setNovoItem("");
  };
  const salvar = () => {
    if (!titulo.trim()) return setErro("Informe o título da tarefa.");
    const vinculo = periciaId || undefined;
    const dados = {
      titulo: titulo.trim(),
      descricao: descricao.trim() || undefined,
      prazo: prazo || undefined,
      prioridade,
      status,
      pericia_id: vinculo,
      checklist: checklist.filter((c) => c.texto.trim()),
      modelo_id: modeloId || undefined,
      prazoSugerido: prazoSugerido && !!prazo,
    };
    if (tarefa) {
      s.updateTarefa(tarefa.id, dados);
      s.addHistorico({ acao: `Tarefa atualizada: ${dados.titulo}`, pericia_id: vinculo });
    } else {
      s.addTarefa(dados);
    }
    aoFechar();
  };
  const excluir = tarefa ? () => { s.deleteTarefa(tarefa.id); aoFechar(); } : undefined;

  return (
    <Dialogo aberto={aberto} aoFechar={aoFechar} titulo={tarefa ? "Editar a tarefa" : "Nova tarefa"}
      rodape={<Rodape aoExcluir={excluir} rotuloExcluir="Excluir a tarefa" aoFechar={aoFechar} aoSalvar={salvar} />}>
      <div className="flex flex-col gap-4" data-dialogo-tarefa>
        {!tarefa && (
          <Campo rotulo="Começar de um modelo" dica="O modelo preenche o título, a descrição, o prazo e o checklist. Dá para ajustar tudo.">
            <select className={CAMPO} value={modeloId} onChange={(e) => (e.target.value ? aplicarModelo(e.target.value) : setModeloId(""))} data-modelo>
              <option value="">Tarefa em branco</option>
              {ETAPAS.map((e) => {
                const doGrupo = TEMPLATES_TAREFA.filter((t) => t.etapa === e);
                return doGrupo.length === 0 ? null : (
                  <optgroup key={e} label={`${e}. ${ETAPA_LABEL[e]}`}>
                    {doGrupo.map((t) => <option key={t.id} value={t.id}>{t.titulo}</option>)}
                  </optgroup>
                );
              })}
              <optgroup label="Outras">
                {TEMPLATES_TAREFA.filter((t) => !t.etapa).map((t) => <option key={t.id} value={t.id}>{t.titulo}</option>)}
              </optgroup>
            </select>
          </Campo>
        )}
        <Campo rotulo="Título"><input className={CAMPO} value={titulo} maxLength={300} onChange={(e) => setTitulo(e.target.value)} data-campo-titulo /></Campo>
        <Campo rotulo="Descrição"><textarea className={`${CAMPO} h-24 py-3`} value={descricao} maxLength={5000} onChange={(e) => setDescricao(e.target.value)} /></Campo>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Campo rotulo="Prazo">
            <input className={CAMPO} type="date" value={prazo} onChange={(e) => { setPrazo(e.target.value); setPrazoSugerido(false); }} data-campo-prazo />
            {prazoSugerido && prazo && <span className="text-xs font-semibold text-alerta-tinta">Prazo sugerido. Ajuste conforme o prazo definido na sua perícia.</span>}
          </Campo>
          <Campo rotulo="Perícia vinculada">
            <select className={CAMPO} value={periciaId} onChange={(e) => setPericiaId(e.target.value)}>
              <option value="">Sem vínculo</option>
              {s.pericias.map((p) => <option key={p.id} value={p.id}>{nomeCurto(p)}</option>)}
            </select>
          </Campo>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Campo rotulo="Prioridade">
            <select className={CAMPO} value={prioridade} onChange={(e) => setPrioridade(e.target.value as Prioridade)}>
              {PRIORIDADES.map((p) => <option key={p} value={p}>{PRIORIDADE_LABEL[p]}</option>)}
            </select>
          </Campo>
          <Campo rotulo="Situação">
            <select className={CAMPO} value={status} onChange={(e) => setStatus(e.target.value as StatusTarefa)} data-campo-situacao>
              {(Object.keys(TAREFA_LABEL) as StatusTarefa[]).map((st) => <option key={st} value={st}>{TAREFA_LABEL[st]}</option>)}
            </select>
          </Campo>
        </div>
        <fieldset className="flex flex-col gap-2.5 border-t border-borda pt-4">
          <span className="text-sm font-semibold text-tinta">Checklist</span>
          {checklist.map((c) => (
            <div key={c.id} className="flex items-center gap-2.5">
              <input type="checkbox" className="size-4 shrink-0 accent-acento" checked={c.feito} aria-label={`Item feito: ${c.texto}`}
                onChange={(e) => setChecklist((l) => l.map((i) => (i.id === c.id ? { ...i, feito: e.target.checked } : i)))} />
              <input className={`${CAMPO} h-10`} value={c.texto} maxLength={300} aria-label="Texto do item"
                onChange={(e) => setChecklist((l) => l.map((i) => (i.id === c.id ? { ...i, texto: e.target.value } : i)))} />
              <button type="button" aria-label={`Remover ${c.texto}`} onClick={() => setChecklist((l) => l.filter((i) => i.id !== c.id))}
                className="grid size-10 shrink-0 place-items-center rounded-full text-cinza hover:bg-chip hover:text-erro-tinta">×</button>
            </div>
          ))}
          <div className="flex items-center gap-2">
            <input className={`${CAMPO} h-10`} value={novoItem} maxLength={300} placeholder="Novo item do checklist" aria-label="Novo item do checklist"
              onChange={(e) => setNovoItem(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); incluirItem(); } }} />
            <button type="button" aria-label="Incluir o item" onClick={incluirItem} className="grid size-10 shrink-0 place-items-center rounded-full border border-borda text-tinta hover:border-acento"><IconeMais /></button>
          </div>
        </fieldset>
        <Erro texto={erro} />
      </div>
    </Dialogo>
  );
}

// ── O prazo ─────────────────────────────────────────────────────────────────────────────────────────────

export function PrazoDialogo({ prazo, aberto, periciaPadrao, aoFechar }: { prazo: Prazo | null; aberto: boolean; periciaPadrao?: string; aoFechar: () => void }) {
  const s = usePerito();
  const [titulo, setTitulo] = useState("");
  const [data, setData] = useState("");
  const [observacao, setObservacao] = useState("");
  const [periciaId, setPericiaId] = useState("");
  const [cumprido, setCumprido] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (!aberto) return;
    setTitulo(prazo?.titulo ?? "");
    setData(prazo?.data ?? "");
    setObservacao(prazo?.observacao ?? "");
    setPericiaId(prazo?.pericia_id ?? periciaPadrao ?? "");
    setCumprido(prazo?.cumprido ?? false);
    setErro(null);
    // Só na abertura: reler a cada mudança da perícia apagaria o que a pessoa está digitando.
  }, [aberto]);

  const salvar = () => {
    if (!titulo.trim() || !data) return setErro("Informe o título e a data do prazo.");
    const vinculo = periciaId || undefined;
    const dados = { titulo: titulo.trim(), data, observacao: observacao.trim() || undefined, pericia_id: vinculo, cumprido };
    if (prazo) {
      s.updatePrazo(prazo.id, dados);
      s.addHistorico({ acao: `Prazo atualizado: ${dados.titulo}`, pericia_id: vinculo });
    } else {
      s.addPrazo(dados);
      s.addHistorico({ acao: `Prazo criado: ${dados.titulo}`, pericia_id: vinculo });
    }
    aoFechar();
  };
  const excluir = prazo ? () => { s.deletePrazo(prazo.id); aoFechar(); } : undefined;

  return (
    <Dialogo aberto={aberto} aoFechar={aoFechar} titulo={prazo ? "Editar o prazo" : "Novo prazo"}
      rodape={<Rodape aoExcluir={excluir} rotuloExcluir="Excluir o prazo" aoFechar={aoFechar} aoSalvar={salvar} />}>
      <div className="flex flex-col gap-4" data-dialogo-prazo>
        <Campo rotulo="Título"><input className={CAMPO} value={titulo} maxLength={300} placeholder="Ex.: Entregar esclarecimentos" onChange={(e) => setTitulo(e.target.value)} data-campo-titulo /></Campo>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Campo rotulo="Data"><input className={CAMPO} type="date" value={data} onChange={(e) => setData(e.target.value)} data-campo-data /></Campo>
          <Campo rotulo="Perícia vinculada">
            <select className={CAMPO} value={periciaId} onChange={(e) => setPericiaId(e.target.value)}>
              <option value="">Sem vínculo</option>
              {s.pericias.map((p) => <option key={p.id} value={p.id}>{nomeCurto(p)}</option>)}
            </select>
          </Campo>
        </div>
        <Campo rotulo="Observação"><textarea className={`${CAMPO} h-24 py-3`} value={observacao} maxLength={2000} placeholder="Detalhes, referências, número da petição…" onChange={(e) => setObservacao(e.target.value)} /></Campo>
        <label className="flex items-center gap-2.5 text-sm font-semibold text-tinta">
          <input type="checkbox" className="size-4 accent-acento" checked={cumprido} onChange={(e) => setCumprido(e.target.checked)} /> Prazo cumprido
        </label>
        <Erro texto={erro} />
      </div>
    </Dialogo>
  );
}

// ── O honorário ─────────────────────────────────────────────────────────────────────────────────────────

/**
 * O lançamento de um honorário, com as retenções do Carnê-Leão. O "Sugerir IRRF" de lá NÃO veio, por decisão do
 * Enzo (03/11): lá ele usava a tabela de maio/2025 escrita no código; o hub tem a tabela no banco (`dp_faixas`) e
 * o redutor de 2026 (Lei 15.270), e as fontes divergem sobre o redutor valer no cálculo de cada mês. O aluno
 * informa o IRRF retido que está no comprovante.
 */
export function HonorarioDialogo({ pericia, honorario, aberto, aoFechar }: { pericia: Pericia; honorario: Honorario | null; aberto: boolean; aoFechar: () => void }) {
  const s = usePerito();
  const [descricao, setDescricao] = useState("");
  const [valor, setValor] = useState("");
  const [recebido, setRecebido] = useState("");
  const [status, setStatus] = useState<Honorario["status"]>("pendente");
  const [vencimento, setVencimento] = useState("");
  const [dataRecebimento, setDataRecebimento] = useState("");
  const [inss, setInss] = useState("");
  const [irrf, setIrrf] = useState("");
  const [fonte, setFonte] = useState("");
  const [documento, setDocumento] = useState("");
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (!aberto) return;
    setDescricao(honorario?.descricao ?? "Honorários periciais");
    setValor(honorario ? String(honorario.valor) : "");
    setRecebido(honorario?.recebido ? String(honorario.recebido) : "");
    setStatus(honorario?.status ?? "pendente");
    setVencimento(honorario?.vencimento ?? "");
    setDataRecebimento(honorario?.dataRecebimento ?? "");
    setInss(honorario?.inss ? String(honorario.inss) : "");
    setIrrf(honorario?.irrf ? String(honorario.irrf) : "");
    setFonte(pericia.fontePagadora ?? "");
    setDocumento(mascaraCpfCnpj(pericia.cpfCnpjFonte ?? ""));
    setErro(null);
    // Só na abertura: reler a cada mudança da perícia apagaria o que a pessoa está digitando.
  }, [aberto]);

  const nValor = numeroDoCampo(valor) ?? 0;
  const nRecebido = numeroDoCampo(recebido) ?? 0;
  const nInss = numeroDoCampo(inss) ?? 0;
  const nIrrf = numeroDoCampo(irrf) ?? 0;

  const trocarSituacao = (st: Honorario["status"]) => {
    setStatus(st);
    if (st === "pago") setRecebido(String(nValor));
    if (st === "pendente") { setRecebido(""); setDataRecebimento(""); }
  };
  const salvar = () => {
    if (!descricao.trim() || nValor <= 0) return setErro("Informe a descrição e o valor do honorário.");
    if ([nRecebido, nInss, nIrrf].some((n) => n < 0)) return setErro("Nenhum valor pode ser negativo.");
    if (nRecebido > nValor) return setErro("O valor recebido não pode ser maior que o valor do honorário.");
    if (nInss + nIrrf > nRecebido) return setErro("As retenções não podem ser maiores que o valor recebido.");
    if (documento && !cpfCnpjValido(documento)) return setErro("O CPF ou CNPJ está incompleto. Complete ou apague o campo.");
    const dados: Omit<Honorario, "id"> = {
      descricao: descricao.trim(),
      valor: nValor,
      recebido: nRecebido,
      status,
      vencimento: vencimento || undefined,
      // Recebeu e a data não foi dita: o dia de hoje em São Paulo, como no editor do cadastro.
      dataRecebimento: dataRecebimento || (nRecebido > 0 ? hoje() : undefined),
      inss: nInss || undefined,
      irrf: nIrrf || undefined,
    };
    if (honorario) {
      s.updateHonorario(pericia.id, honorario.id, dados);
      s.addHistorico({ acao: `Honorário atualizado: ${dados.descricao}`, pericia_id: pericia.id, detalhe: `${formatBRL(nRecebido)} de ${formatBRL(nValor)}` });
    } else {
      s.addHonorario(pericia.id, dados);
    }
    // A fonte pagadora é da perícia: só se grava quando mudou, para não escrever a perícia a cada honorário.
    const fonteNova = fonte.trim() || undefined;
    const documentoNovo = documento.replace(/\D/g, "") || undefined;
    if (fonteNova !== (pericia.fontePagadora || undefined) || documentoNovo !== (pericia.cpfCnpjFonte?.replace(/\D/g, "") || undefined)) {
      s.updatePericia(pericia.id, { fontePagadora: fonteNova, cpfCnpjFonte: documentoNovo });
    }
    aoFechar();
  };
  const excluir = honorario ? () => { s.deleteHonorario(pericia.id, honorario.id); aoFechar(); } : undefined;

  return (
    <Dialogo aberto={aberto} aoFechar={aoFechar} titulo={honorario ? "Editar o honorário" : "Novo honorário"}
      rodape={<Rodape aoExcluir={excluir} rotuloExcluir="Excluir o honorário" aoFechar={aoFechar} aoSalvar={salvar} />}>
      <div className="flex flex-col gap-4" data-dialogo-honorario>
        <Campo rotulo="Descrição"><input className={CAMPO} value={descricao} maxLength={300} onChange={(e) => setDescricao(e.target.value)} /></Campo>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Campo rotulo="Valor (R$)"><input className={`${CAMPO} text-right tabular-nums`} type="number" step="0.01" min="0" inputMode="decimal" value={valor} onChange={(e) => setValor(e.target.value)} data-campo-valor /></Campo>
          <Campo rotulo="Valor recebido (R$)"><input className={`${CAMPO} text-right tabular-nums`} type="number" step="0.01" min="0" inputMode="decimal" value={recebido} onChange={(e) => setRecebido(e.target.value)} data-campo-recebido /></Campo>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Campo rotulo="Previsão de depósito"><input className={CAMPO} type="date" value={vencimento} onChange={(e) => setVencimento(e.target.value)} /></Campo>
          <Campo rotulo="Data do recebimento"><input className={CAMPO} type="date" value={dataRecebimento} onChange={(e) => setDataRecebimento(e.target.value)} /></Campo>
        </div>
        <Campo rotulo="Situação">
          <select className={CAMPO} value={status} onChange={(e) => trocarSituacao(e.target.value as Honorario["status"])} data-campo-situacao>
            <option value="pendente">Ainda não recebido</option>
            <option value="parcial">Recebido em parte</option>
            <option value="pago">Recebido integralmente</option>
          </select>
        </Campo>
        <div className="grid grid-cols-1 gap-4 border-t border-borda pt-4 sm:grid-cols-2">
          <Campo rotulo="INSS retido (R$)"><input className={`${CAMPO} text-right tabular-nums`} type="number" step="0.01" min="0" inputMode="decimal" placeholder="0,00" value={inss} onChange={(e) => setInss(e.target.value)} /></Campo>
          <Campo rotulo="IRRF retido (R$)"><input className={`${CAMPO} text-right tabular-nums`} type="number" step="0.01" min="0" inputMode="decimal" placeholder="0,00" value={irrf} onChange={(e) => setIrrf(e.target.value)} /></Campo>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Campo rotulo="Fonte pagadora"><input className={CAMPO} value={fonte} maxLength={200} onChange={(e) => setFonte(e.target.value)} /></Campo>
          <Campo rotulo="CPF ou CNPJ">
            <input className={`${CAMPO} tabular-nums`} value={documento} inputMode="numeric" placeholder="00.000.000/0000-00" onChange={(e) => setDocumento(mascaraCpfCnpj(e.target.value))} aria-invalid={documento ? !cpfCnpjValido(documento) : undefined} />
            {documento && !cpfCnpjValido(documento) && <span className="text-xs font-semibold text-erro-tinta">Documento incompleto</span>}
          </Campo>
        </div>
        <div className="flex items-center justify-between rounded-cf bg-aviso-fundo px-4 py-3" data-liquido>
          <span className="text-[11px] font-bold tracking-[0.14em] text-aviso-tinta uppercase">Líquido recebido</span>
          <span className="font-display text-lg font-bold text-aviso-tinta tabular-nums">{formatBRL(nRecebido - nInss - nIrrf)}</span>
        </div>
        <Erro texto={erro} />
      </div>
    </Dialogo>
  );
}
